import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { session, sessionPromise, startNgSession, type NextGraphSession } from '../utils/ngSession';

type NgStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

interface NextGraphContextValue {
  status: NgStatus;
  session: NextGraphSession | undefined;
  error: string | undefined;
}

const NextGraphContext = createContext<NextGraphContextValue>({
  status: 'disconnected',
  session: undefined,
  error: undefined,
});

// Track whether the session start has been triggered (module-level to survive re-renders)
let ngInitStarted = false;

// Detect if we're running inside the NG broker iframe
const isInsideBroker = typeof window !== 'undefined' && window.self !== window.top;

export function NextGraphProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<NgStatus>(session ? 'connected' : 'disconnected');
  const [ngSession, setNgSession] = useState<NextGraphSession | undefined>(session);
  const [error, setError] = useState<string | undefined>();

  // Start the session ALWAYS, inside the broker iframe and standalone alike.
  //
  // It used to be iframe-only, because outside the broker starting the session
  // REDIRECTS the page and that had to stay a deliberate act — the user clicked
  // "Entrer" on the app's own access screen. That screen is gone: signing in is
  // `ensureIdentity()`, and the SDK shows whatever a user must see. Nothing is
  // left to click, so the redirect must fire on its own.
  //
  // And it is no longer optional: a session arrives ONLY through the SDK's `init`,
  // and `ensureIdentity()` awaited before that call has been made throws. An
  // unconditional start is what the contract asks for, in every context.
  //
  // `startNgSession` is itself idempotent (AuthGate calls it too, right before its
  // await, so the order does not depend on React's effect ordering). What this
  // provider adds on top is the CONNECTION STATUS the app displays.
  const startSession = useCallback(() => {
    if (ngInitStarted) return;
    ngInitStarted = true;

    console.log(`[NG] session start (${isInsideBroker ? 'broker iframe' : 'standalone → redirect'})`);
    setStatus('connecting');
    setError(undefined);
    startNgSession();

    sessionPromise
      .then((s) => {
        // Nothing is handed to the SDK here: the session is the SDK's, captured by
        // its own `init`, and no call takes one. What this context keeps is the
        // opaque handle the app reads a `session_id` off for the `docs`
        // primitives, plus the connection status the screens display. No store id
        // crosses this boundary — placement is named by scope alone.
        console.log('[NG] Session obtained — connected');
        setNgSession(s);
        setStatus('connected');
      })
      .catch((err) => {
        console.error('[NG] Connection failed:', err);
        setError(err?.message || 'Connexion NextGraph impossible');
        setStatus('error');
      });
  }, []);

  useEffect(() => { startSession(); }, [startSession]);

  // NO retry on the return from the broker round-trip. That journey belongs to the
  // SDK: coming back to the page finds its barrier live again and prefilled, and
  // confirming it hands the page over a second time — our page is never reloaded
  // and nothing outside the barrier is touched. A `pageshow` handler of ours that
  // re-started the session would be a second actor driving the same journey, and
  // it would redirect out from under the barrier the SDK just put up.

  return (
    <NextGraphContext.Provider value={{ status, session: ngSession, error }}>
      {children}
    </NextGraphContext.Provider>
  );
}

export function useNextGraph() {
  return useContext(NextGraphContext);
}
